<template>
  <main class="p-passwordchange">
    <section class="p-passwordchange__wrap">
      <h2 class="c-regist__title">{{ t('PasswordChangePage.title') }}</h2>
      <RegisterFlow :steps="4" :nowStep="3" />
      <p class="c-regist__comment">{{ t('PasswordChangePage.message') }}</p>
      <dl class="p-passwordchange__lists">
        <div class="p-passwordchange__list">
          <dt class="p-passwordchange__list-title">
            <span class="-text">{{ t('PasswordChangePage.password') }}</span>
            <span class="-required">{{ t('PasswordChangePage.must') }}</span>
            <span class="-comment">{{ t('PasswordChangePage.validate.password') }}</span>
          </dt>
          <dd>
            <input
              v-model="state.password"
              class="p-passwordchange__list-data-item"
              type="password"
              :placeholder="t('EnterAccountPage.password')"
            />
          </dd>
        </div>
        <div class="p-passwordchange__list">
          <dt class="p-passwordchange__list-title">
            <span class="-text">{{ t('PasswordChangePage.password2') }}</span>
            <span class="-required">{{ t('PasswordChangePage.must') }}</span>
          </dt>
          <dd>
            <input
              v-model="state.confirmPassword"
              class="p-passwordchange__list-data-item"
              type="password"
              :placeholder="t('PasswordChangePage.password')"
            />
          </dd>
        </div>
      </dl>
      <div class="p-passwordchange__link">
        <button
          @click="onClickEnterAccount"
          :disabled="!isEnableButton"
          :class="{ isDisabled: !isEnableButton }"
          class="c-button"
        >
          <span class="c-button__text">{{ t('PasswordChangePage.confirm') }}</span>
        </button>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import axios from 'axios'
import StoreUtil from '@/stores/StoreUtil'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'

export default defineComponent({
  name: 'PasswordChangePage',
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const state = reactive({
      tokenId: '',
      password: '',
      confirmPassword: '',
      isSubmitting: false,
    })
    const regexpPassword = /^[a-zA-Z0-9-_.@!#$%&'*+/=?^`{|}~]{8,32}$/
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')
    const passwordChangeStore = StoreUtil.useStore('PasswordChangeStore')
    const ErrorStore = StoreUtil.useStore('ErrorStore')
    const { checkMaintenanceStatusByApiBaseUrl } = useMaintenanceStatus()

    const isEnableButton = computed(
      () =>
        state.password &&
        state.confirmPassword &&
        regexpPassword.test(state.password) &&
        state.password === state.confirmPassword &&
        !state.isSubmitting,
    )
    onMounted(async () => {
      const isAvailable = await checkMaintenanceStatusByApiBaseUrl()
      if (!isAvailable) {
        router.replace({ name: 'MaintenancePage' })
        return
      }
      if (
        !router.currentRoute.value.query.tokenId ||
        typeof router.currentRoute.value.query.tokenId !== 'string'
      ) {
        ErrorStore.setConfig('tokenInvalid.changePassword')
        router.replace({ name: 'ErrorPage' })
        return
      }
      passwordChangeStore.tokenId = router.currentRoute.value.query.tokenId
    })
    /**
     * APIに値を送信する。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1569259643/API
     * @return 送信結果。
     */
    const submit = async () => {
      const serviceRegistrationRes = await axios
        .put(
          `${process.env.VUE_APP_API_BASE_URL}/manage/password_reset_token/${passwordChangeStore.tokenId}/reset`,
          {
            password: state.password,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes }
        })
      return serviceRegistrationRes
    }
    return {
      state,
      isEnableButton,
      t,
      async onClickEnterAccount() {
        if (state.isSubmitting) return
        try {
          state.isSubmitting = true
          const submitResult = await submit()
          if (submitResult.isSuccess) {
            router.replace({ name: 'PasswordChangeCompletePage' })
            return
          }
          let dialogOptions = {}
          switch (submitResult.res.response.data.error_code) {
            case '404_0001': {
              dialogOptions = {
                title: t('PasswordChangePage.dialog.invalidUser.title'),
                message: t('PasswordChangePage.dialog.invalidUser.message'),
                status: 'error',
              }
              break
            }
            case '400_0042': {
              ErrorStore.setConfig('tokenInvalid.changePassword')
              router.replace({ name: 'ErrorPage' })
              return
            }
            case 'NETWORK_ERROR': {
              dialogOptions = {
                title: t('dialog.netWorkError.title'),
                message: t('dialog.netWorkError.message'),
                status: 'error',
              }
              break
            }
            default: {
              dialogOptions = {
                title: t('dialog.unknownError.title'),
                message: t('dialog.unknownError.message', [
                  submitResult.res.response.data.error_code,
                ]),
                status: 'error',
              }
              break
            }
          }
          await messageDialogStore.openWithSettings(dialogOptions)
        } finally {
          state.isSubmitting = false
        }
      },
    }
  },
  components: {
    RegisterFlow,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
</style>
